import { motion } from "motion/react";
import SectionSepratorHeader from "../../components/ui/section-seprator-header";
import Noise from "../../components/core/noise";

// Data for Metal FX requirement
const STRATEGY_DATA = [
  {
    id: 1,
    heading: "Consultation & Structural Audit",
    items: [
      "On-site visibility & ordinance analysis",
      "Structural integrity & wind-load assessment",
      "Custom permit procurement & coordination",
      "Material durability scouting",
    ],
  },
  {
    id: 2,
    heading: "Industrial Grade Fabrication",
    items: [
      "Precision CNC metal cutting & forming",
      "Advanced LED illumination engineering",
      "High-durability powder coating finish",
      "Internal structural frame welding",
    ],
  },
  {
    id: 3,
    heading: "Logistics & Site Mobilization",
    items: [
      "Secure custom crating for safe transport",
      "Specialized heavy-lifting equipment prep",
      "Traffic & access management planning",
      "Final pre-install quality inspection",
    ],
  },
  {
    id: 4,
    heading: "Precision Field Installation",
    items: [
      "White-glove positioning & mounting",
      "Electrical system final integration",
      "Comprehensive structural load testing",
      "Post-install safety certification",
    ],
  },
];

const StrategyCard = ({
  id,
  heading,
  items,
  index,
}: {
  id: number;
  heading: string;
  items: string[];
  index: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98, y: 40 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        duration: 0.8,
        delay: index * 0.1,
        ease: [0.16, 1, 0.3, 1],
      }}
      viewport={{ once: true, margin: "-100px" }}
      className="relative px-6 py-10 md:px-10 md:py-12 bg-[#1a1c1c]/40 backdrop-blur-2xl border border-white/10 rounded-4xl hover:border-[#4c94ba]/60 hover:shadow-[0_0_30px_rgba(76,148,186,0.15)] transition-all duration-700 group overflow-hidden flex flex-col h-full"
    >
      <Noise />
      <div className="relative z-10 flex flex-col h-full">
        <div className="w-14 h-14 bg-[#4c94ba]/20 rounded-2xl flex items-center justify-center mb-8 border border-[#4c94ba]/30 shadow-inner group-hover:scale-110 group-hover:bg-[#4c94ba]/30 transition-all duration-500">
          <span className="text-[#4c94ba] font-bold text-2xl">{id}</span>
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 uppercase tracking-tight leading-none group-hover:text-[#4c94ba] transition-colors duration-500">
          {heading}
        </h3>
        <ul className="flex flex-col gap-5 list-none mt-auto">
          {items.map((item, i) => (
            <li
              key={i}
              className="text-zinc-300 font-medium text-sm md:text-base leading-relaxed flex items-start gap-4 group/item"
            >
              <div className="w-2 h-2 bg-[#4c94ba] rounded-full mt-2 shrink-0 group-hover/item:scale-150 group-hover/item:shadow-[0_0_10px_#4c94ba] transition-all duration-300" />
              <span className="opacity-80 group-hover/item:opacity-100 transition-opacity">
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Interactive Background Glow */}
      <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-[#4c94ba]/10 blur-[80px] group-hover:bg-[#4c94ba]/20 group-hover:scale-125 transition-all duration-1000" />
    </motion.div>
  );
};

export const SignageStrategySection = () => {
  return (
    <section className="w-full bg-[#111212]  overflow-hidden">
      <div className="mx-auto px-2.5 md:px-6">
        <div className="mb-16 lg:mb-24">
          <SectionSepratorHeader
            title="STRATEGIC EXECUTION"
            subtitle={
              <>
                Engineered for Performance. <br /> Installed for Impact.
              </>
            }
            description="Our professional workflow for industrial signage ensures every project meets structural integrity standards while maximizing brand visibility."
            className="px-0! mb-0!"
          />
        </div>
        <div className="relative w-full rounded-3xl p-4 md:p-8 lg:p-16 overflow-hidden border border-white/5 bg-[#111212] shadow-2xl">
          <div className="absolute inset-0 w-full h-full opacity-20 lg:opacity-30">
            <div className="grid grid-cols-1 lg:grid-cols-1 w-full h-full gap-2">
              <img
                src="https://cdn.shopify.com/s/files/1/0994/4979/8950/files/pexels-kateryna-babaieva-1423213-2880871.jpg?v=1773202978"
                alt="Fabrication"
                className="w-full h-full object-cover "
              />
            </div>
          </div>
          <div className="relative z-20 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 max-w-7xl mx-auto items-stretch">
            {STRATEGY_DATA.map((item, index) => (
              <StrategyCard
                key={item.id}
                id={item.id}
                heading={item.heading}
                items={item.items}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
